import type { LinkHTMLAttributes } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { joinURL } from 'ufo';
import { useBrand } from '@/hooks/pwa/use-brand';
import useUserPlatform, { useDisplayMode } from '@/hooks/use-user-platform';
import { BASE_URL } from '@/utils/constants';
import { getCodeByBrand } from '@/utils/language-code';

export default function AppHelmet() {
  const { i18n } = useTranslation();
  const { isIOS, isAndroid, isStandalone, mode } = useUserPlatform();
  const brand = useBrand();
  const display = useDisplayMode();

  const { locale, glyph, icon, splashscreens } = getCodeByBrand(brand.key, i18n.language);

  return (
    <Helmet
      defaultTitle={brand.name}
      titleTemplate={`%s | ${brand.name}`}
      htmlAttributes={{
        'data-brand': brand.key,
        'data-theme': brand.theme ?? 'whitelabel',
        'data-mode': mode,
        'data-display': display,
        'data-standalone': isStandalone ? 'true' : 'false',
        'data-glyph': glyph,
        'data-platform': isIOS ? 'ios' : isAndroid ? 'android' : 'other',
      }}>
      <meta
        name='theme-color'
        content='#fff'
      />
      <meta
        name='application-name'
        content={brand.name}
      />
      <link
        rel='manifest'
        href={joinURL(BASE_URL, `/manifest/manifest.${locale}.json`)}
      />
      {iconLinks.map(({ href, ...link }) => (
        <link
          key={href}
          {...link}
          href={icon ? href?.replace(/\.png$/, `.${icon}.png`) : href}
        />
      ))}
      {splashscreenLinks.map(({ href, ...link }) => (
        <link
          key={href}
          {...link}
          href={splashscreens ? href?.replace('/splashscreens/', `/splashscreens/${splashscreens}/`) : href}
        />
      ))}
    </Helmet>
  );
}

const iconLinks: LinkHTMLAttributes<HTMLLinkElement>[] = [
  { rel: 'icon', href: '/icon-256.rounded.png', type: 'image/png' },
  { rel: 'apple-touch-icon', href: '/icon-180.rounded.png', sizes: '180x180' },
  { rel: 'apple-touch-icon', href: '/icon-192.rounded.png', sizes: '192x192' },
  { rel: 'apple-touch-icon', href: '/icon-256.rounded.png', sizes: '256x256' },
];

const splashscreenLinks: LinkHTMLAttributes<HTMLLinkElement>[] = [
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-2048-2732.png',
    media:
      '(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1668-2388.png',
    media:
      '(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1536-2048.png',
    media:
      '(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1488-2266.png',
    media:
      '(device-width: 744px) and (device-height: 1133px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1640-2360.png',
    media:
      '(device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1668-2224.png',
    media:
      '(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1620-2160.png',
    media:
      '(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1320-2868.png',
    media:
      '(device-width: 440px) and (device-height: 956px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1206-2622.png',
    media:
      '(device-width: 402px) and (device-height: 874px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1290-2796.png',
    media:
      '(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1179-2556.png',
    media:
      '(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1284-2778.png',
    media:
      '(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1170-2532.png',
    media:
      '(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1125-2436.png',
    media:
      '(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1242-2688.png',
    media:
      '(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-828-1792.png',
    media:
      '(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-1242-2208.png',
    media:
      '(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-750-1334.png',
    media:
      '(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
  {
    rel: 'apple-touch-startup-image',
    href: '/splashscreens/apple-splash-640-1136.png',
    media:
      '(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)',
  },
];

/**
 *   <link rel='icon' href='/icon-256.rounded.png' type="image/png"/>
 *   <link rel='apple-touch-icon' href='/icon-180.rounded.png' sizes='180x180'/>
 *   <link rel='apple-touch-icon' href='/icon-192.rounded.png' sizes='192x192'/>
 *   <link rel='apple-touch-icon' href='/icon-256.rounded.png' sizes='256x256'/>
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-2048-2732.png"
 *         media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1668-2388.png"
 *         media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1536-2048.png"
 *         media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1488-2266.png"
 *         media="(device-width: 744px) and (device-height: 1133px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1640-2360.png"
 *         media="(device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1668-2224.png"
 *         media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1620-2160.png"
 *         media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1320-2868.png"
 *         media="(device-width: 440px) and (device-height: 956px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1206-2622.png"
 *         media="(device-width: 402px) and (device-height: 874px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1290-2796.png"
 *         media="(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1179-2556.png"
 *         media="(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1284-2778.png"
 *         media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1170-2532.png"
 *         media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1125-2436.png"
 *         media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1242-2688.png"
 *         media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-828-1792.png"
 *         media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-1242-2208.png"
 *         media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-750-1334.png"
 *         media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 *   <link rel="apple-touch-startup-image" href="/splashscreens/apple-splash-640-1136.png"
 *         media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)">
 */
